import * as THREE from 'three';
import { ORC_FACE_DEFAULT, type OrcFaceMorph } from '../../../content/characters/rigs/orc-face.js';
import type { HumanProportions } from '../../../content/characters/rigs/human.js';
import { lowerMouthSurface, type HeadPiece } from './human-head.js';

/** Roots are planted inside the lower lip at the canine positions; the tip bends up and slightly inward. */
export function orcTuskCurves(p: HumanProportions, m: OrcFaceMorph = ORC_FACE_DEFAULT) {
  return [-1, 1].map(side => {
    const surface = lowerMouthSurface(p, side * m.tuskSpread);
    const root = new THREE.Vector3(surface.x, surface.y - .002, surface.z - m.tuskRadius * .65);
    const curve = new THREE.CubicBezierCurve3(root,
      root.clone().add(new THREE.Vector3(side * .001, m.tuskLength * .35, m.tuskForward)),
      root.clone().add(new THREE.Vector3(side * .001, m.tuskLength * .78, m.tuskForward)),
      root.clone().add(new THREE.Vector3(-side * .002, m.tuskLength, m.tuskForward - m.tuskCurl)));
    return { surface, root, curve };
  });
}

/** Closed, smoothly tapered enamel surfaces, with each ring perpendicular to the curved tooth rather than horizontal. */
export function orcTusks(p: HumanProportions, m: OrcFaceMorph = ORC_FACE_DEFAULT): HeadPiece[] {
  return orcTuskCurves(p, m).map(({ root, curve }) => {
    const rings = 16, around = 16, positions: number[] = [], indices: number[] = [], colors: number[] = [];
    for (let row = 0; row <= rings; row++) {
      const t = row / rings, r = m.tuskRadius * (1 - t) ** .8;
      const at = curve.getPoint(t), tangent = curve.getTangent(t).normalize();
      const normal = new THREE.Vector3(1, 0, 0).addScaledVector(tangent, -tangent.x).normalize(), across = normal.clone().cross(tangent).normalize();
      for (let column = 0; column <= around; column++) {
        const angle = column / around * Math.PI * 2;
        const point = at.clone().addScaledVector(normal, Math.cos(angle) * r).addScaledVector(across, Math.sin(angle) * r);
        positions.push(...point.toArray()); colors.push(.92 + .08 * t, .87 + .12 * t, .77 + .20 * t);
        if (row < rings && column < around) { const a = row * (around + 1) + column, b = a + around + 1; indices.push(a, b, a + 1, a + 1, b, b + 1); }
      }
    }
    // The buried root is capped; the last ring closes to the tip.
    const cap = positions.length / 3; positions.push(...root.toArray()); colors.push(.92, .87, .77);
    for (let column = 0; column < around; column++) indices.push(cap, column, column + 1);
    const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3)); geometry.setIndex(indices); geometry.computeVertexNormals();
    const piece: HeadPiece = { material: 'ivory', positions: new Float32Array(positions), normals: new Float32Array(geometry.getAttribute('normal').array), indices: new Uint32Array(indices), colors: new Float32Array(colors) };
    geometry.dispose(); return piece;
  });
}
